import { Dialog, DialogContent } from "@/components/ui/dialog";
import { InterestListHowItWorksDemo } from "./InterestListHowItWorksDemo";
import { cn } from "@/lib/utils";
import { panelAccentDot, panelPill } from "@/lib/panel-ui";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreateList?: () => void;
}

function Step({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="h-5 w-5 rounded-full bg-secondary text-foreground text-xs font-semibold flex items-center justify-center shrink-0 tabular-nums">
          {n}
        </span>
        <p className="text-xs font-semibold text-foreground leading-snug">{title}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed pl-7">{children}</p>
    </div>
  );
}

export function InterestListHowItWorksModal({ open, onOpenChange, onCreateList }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent mobilePresentation="fullscreen" className="max-w-4xl p-0 overflow-hidden rounded-[28px] border border-border/70 bg-white dark:bg-[#18181b] shadow-2xl">
        {/* Cabeçalho */}
        <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-border/50">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight text-foreground font-display flex items-center gap-2">
            <span className={panelAccentDot} />
            Como funciona a lista de interesse
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Colete cadastros prévios do seu público e organize a abertura de vendas do seu evento.
          </p>
        </div>

        {/* Conteúdo: Demonstração e Passos */}
        <div className="max-h-[78vh] sm:max-h-[74vh] overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:grid-rows-[minmax(0,1fr)]">
          {/* Esquerda: Demonstração visual de 10s */}
          <div className="p-4 sm:p-5 lg:flex lg:items-center lg:min-h-0">
            <InterestListHowItWorksDemo />
          </div>

          {/* Direita: Etapas explicativas */}
          <div className="border-t lg:border-t-0 lg:border-l border-border/50 bg-secondary/15 lg:min-h-0 lg:overflow-y-auto">
            <div className="p-5 space-y-4">
              <Step n={1} title="Criação e link de divulgação">
                Defina o nome do formulário e os campos que precisa solicitar, como e-mail e WhatsApp. Um link direto para a página é gerado para divulgação.
              </Step>

              <Step n={2} title="Cadastro de interessados">
                O público acessa o formulário e registra os dados de contato. Você acompanha as novas inscrições diretamente pelo painel.
              </Step>

              <Step n={3} title="Aviso de abertura e exportação">
                No momento do lançamento, utilize a base cadastrada para enviar o link de compra ou exporte o arquivo CSV com todos os dados.
              </Step>

              <div className="pt-2 border-t border-border/40">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Você pode configurar limite máximo de participantes e data de encerramento automático em cada lista.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Rodapé */}
        <div className="px-5 sm:px-6 py-3.5 sm:py-4 border-t border-border/50 bg-secondary/20 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <p className="text-[11px] text-muted-foreground text-center sm:text-left">
            Disponível para qualquer evento cadastrado no TicketHall.
          </p>
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className={cn(
                panelPill,
                "rounded-full h-10 px-5 text-xs font-medium border border-border/80 bg-background hover:bg-secondary transition-all shrink-0 active:scale-[0.97] w-full sm:w-auto text-foreground"
              )}
            >
              Fechar
            </button>
            {onCreateList && (
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onCreateList();
                }}
                className={cn(
                  panelPill,
                  "rounded-full h-10 px-5 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all shrink-0 active:scale-[0.97] w-full sm:w-auto shadow-xs"
                )}
              >
                Criar lista de interesse
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default InterestListHowItWorksModal;
